import {Box, Button, Flex, Text, VStack} from '@chakra-ui/react';
import {hiddenScrollBarStyles, useCartContext} from 'components/providers';
import {AlertDialog, Price} from 'components/shared';
import {useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {FC, useReducer} from 'react';
import ImageUI from 'ui/ImageUI';

import {IRestaurantResponse} from '../../../../types';
import CartHeader from './CartHeader';
import {CartItemsMetro} from './CartItemsMetro';
import {CartItemsMetroRestaurant} from './CartItemsRestaurant';

interface CartAsideProps {
  provider?: IRestaurantResponse;
}

const CartAside: FC<CartAsideProps> = ({provider}) => {
  const {cartItems, cartItemsPrice, clearCart, cartProvider} = useCartContext();
  const {push} = useRouter();
  const {t} = useTranslation();

  const [isOpenAlert, toggleOpenAlert] = useReducer((a) => !a, false);

  const onNextStep = () => {
    push('/checkout');
  };

  const isEmptyCart = cartItems.length === 0;

  const onToggleOpenAlert = () => {
    toggleOpenAlert();
  };

  const isMetro = cartProvider?.slug === 'metro';

  return (
    <>
      <Box
        as="aside"
        position="sticky"
        top="100px"
        height="auto"
        maxHeight="90vh"
        backgroundColor="white"
        borderRadius="16px"
        // boxShadow="0px 2px 8px rgba(0, 0, 0, 0.1)"
        minW="350px"
        maxW="350px"
        display="flex"
        flexDirection="column"
      >
        <CartHeader
          providerName={provider?.name || 'Metro'}
          t={t}
          onOpenAlert={onToggleOpenAlert}
          isEmptyCart={isEmptyCart}
        />
        <Box flex="1 1 0" overflow="hidden" display="flex" flexDirection="column">
          {isEmptyCart ? (
            <Flex align="center" justify="center" h="100%" flex="1">
              <VStack
                textAlign="center"
                spacing={0}
                w={['70%', '50%']}
                mx="auto"
                align="center"
                justify="center"
                h="100%"
              >
                <ImageUI src="/icons/empty-cart.svg" h={['300px', '50%']} alt="Empty cart image" />
                <Text as="h1" fontSize="24px" pb="30px">
                  {t('YOUR_CART_IS_EMPTY')}
                </Text>
              </VStack>
            </Flex>
          ) : (
            <Box overflowY="auto" flex="1" sx={hiddenScrollBarStyles}>
              {isMetro ? <CartItemsMetro /> : <CartItemsMetroRestaurant />}
            </Box>
          )}
        </Box>
        <Box
          p="16px"
          borderTop="1px solid"
          borderColor="gray.100"
          justifyContent="flex-end"
          position="sticky"
          bottom="0"
          backgroundColor="white"
          zIndex={1}
          borderRadius="16px"
        >
          {!isEmptyCart && (
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="16px" fontWeight="600">
                {t('ORDER_SUMMARY')} {isMetro ? `(${cartItems.length} ${t('PRODUCTS')})` : ''}
              </Text>
              <Text fontSize="24px" fontWeight="700">
                <Price value={cartItemsPrice} />
              </Text>
            </Flex>
          )}

          <Button width="100%" variant="primary" isDisabled={cartItems.length === 0} onClick={onNextStep}>
            {t('CONTINUE')}
          </Button>
        </Box>
      </Box>
      <AlertDialog title={t('CLEAR_CART_TITLE')} onClose={onToggleOpenAlert} isOpen={isOpenAlert} onConfirm={clearCart}>
        {t('CLEAR_CART_DESCRIPTION')}
      </AlertDialog>
    </>
  );
};

export default CartAside;
